import { useEffect, useRef, useState } from "react";
import { Copy } from "lucide-react";
import { copyText } from "@/components/room/RoomHeader";
import { CatFace } from "@/components/yarn/Cat";
import { YarnBall } from "@/components/yarn/YarnBall";
import { yarnColor } from "@/lib/api";

export const storyAsText = (story) => story.map((s) => s.text).join(" ");

const POUNCE_MS = 550;

function Caption({ line, color, weaving }) {
  if (line.kind === "starter") return <span style={{ color }}>The opening line</span>;
  if (weaving) return <span style={{ color }}>{line.author}'s cat is weaving it in...</span>;
  return <span style={{ color }}>{line.author} · {line.votes} vote{line.votes === 1 ? "" : "s"}</span>;
}

function WovenText({ line, onProgress, onDone }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const total = line.text.length;
    const duration = Math.min(4500, 1300 + total * 24);
    let raf;
    let start;
    const step = (t) => {
      start = start ?? t;
      const p = Math.min(1, (t - start) / duration);
      setCount(Math.round(p * total));
      onProgress();
      if (p < 1) raf = requestAnimationFrame(step);
      else setTimeout(onDone, 350);
    };
    const wait = setTimeout(() => { raf = requestAnimationFrame(step); }, POUNCE_MS);
    return () => { clearTimeout(wait); cancelAnimationFrame(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <p className="font-story leading-relaxed text-base sm:text-lg" data-testid="weaving-line">
      {line.text.slice(0, count)}
      <span className="cat-pounce inline-flex items-end align-middle ml-1 -mb-1" data-testid="weaving-cat">
        <span className="cat-trot inline-flex items-end">
          <YarnBall size={14} color={yarnColor(line.uid)} className="-mr-1 mb-0.5" />
          <CatFace id={line.uid} look={line.cat} size={32} />
        </span>
      </span>
    </p>
  );
}

export function StoryLine({ line, index, weaving, onProgress, onDone }) {
  const starter = line.kind === "starter";
  const color = starter ? "#2C221E" : yarnColor(line.uid);
  return (
    <div className="relative rise-in pb-5" data-testid={`story-line-${index}`}>
      <span className="absolute -left-10 top-0">
        {starter ? <YarnBall size={26} color={color} /> : <CatFace id={line.uid} look={line.cat} size={30} />}
      </span>
      {weaving ? (
        <WovenText line={line} onProgress={onProgress} onDone={onDone} />
      ) : (
        <p className={`font-story leading-relaxed ${starter ? "text-xl sm:text-2xl" : "text-base sm:text-lg"}`}>{line.text}</p>
      )}
      <p className="mt-1 text-xs font-display uppercase tracking-wider">
        <Caption line={line} color={color} weaving={weaving} />
      </p>
    </div>
  );
}

export function StoryThread({ story }) {
  const boxRef = useRef(null);
  const base = useRef(story.length);
  const prevLen = useRef(story.length);
  const [woven, setWoven] = useState({});
  if (story.length < prevLen.current) base.current = Math.min(base.current, story.length);
  prevLen.current = story.length;

  const toBottom = (smooth) => {
    const el = boxRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  };
  useEffect(() => toBottom(true), [story.length]);

  const keyOf = (line, i) => `${i}-${line.round}-${line.text.length}`;

  return (
    <div className="patch p-5 sm:p-7" data-testid="story-thread">
      <div className="flex items-center justify-between mb-4">
        <p className="font-display text-sm uppercase tracking-widest text-[var(--yarn-muted)]">
          The story so far {story.length > 0 && `· ${story.length} line${story.length === 1 ? "" : "s"}`}
        </p>
        {story.length > 0 && (
          <button className="btn-soft h-9 px-3 text-xs inline-flex items-center gap-1" onClick={() => copyText(storyAsText(story), "Story copied")} data-testid="copy-story-button">
            <Copy size={13} /> Copy
          </button>
        )}
      </div>
      {story.length === 0 ? (
        <div className="py-8 text-center" data-testid="story-empty">
          <YarnBall size={44} color="#E6D7C3" className="mx-auto" />
          <p className="font-story italic text-lg text-[var(--yarn-muted)] mt-3">Your story will unspool here once the host starts.</p>
        </div>
      ) : (
        <div ref={boxRef} className="relative pl-10 max-h-[44vh] lg:max-h-[52vh] overflow-y-auto scroll-soft pr-1 pt-1">
          <span className="thread-line" />
          {story.map((line, i) => {
            const key = keyOf(line, i);
            const weaving = line.kind === "line" && i >= base.current && !woven[key];
            return (
              <StoryLine key={key} line={line} index={i} weaving={weaving}
                onProgress={() => toBottom(false)} onDone={() => setWoven((w) => ({ ...w, [key]: true }))} />
            );
          })}
        </div>
      )}
    </div>
  );
}
